'use client';

import 'prosemirror-view/style/prosemirror.css';

import { useMemo, useState } from 'react';
import { ALL_MARKS, ALL_NODES } from '@ametista-ai/editor/constants';
import { EmojiMenuTrigger } from '@ametista-ai/editor/extensions/EmojiMenuTrigger';
import { MentionMenuTrigger } from '@ametista-ai/editor/extensions/MentionMenuTrigger';
import { PasteHandler } from '@ametista-ai/editor/extensions/PasteHandler';
import { Placeholder } from '@ametista-ai/editor/extensions/Placeholder';
import { EmojiMenuContext, EmojiMenuStore } from '@ametista-ai/editor/plugins/EmojiPlugin/store';
import {
  MentionMenuContext,
  MentionMenuStore,
} from '@ametista-ai/editor/plugins/MentionPlugin/store';
import { createSchema } from '@ametista-ai/editor/schema';
import { RichEditorTemplate } from '@ametista-ai/editor/types';
import {
  getCommandsFromExtensions,
  getInputRulesFromExtensions,
  getPluginsFromExtensions,
  parseContentToDoc,
} from '@ametista-ai/editor/utils';
import { ProseMirror, ProseMirrorDoc, reactKeys } from '@handlewithcare/react-prosemirror';
import { baseKeymap } from 'prosemirror-commands';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { inputRules, wrappingInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { EditorState } from 'prosemirror-state';
import { useTranslation } from 'react-i18next';
import { Stack, StackProps } from '@mantine/core';
import ErrorBoundary from '@/ErrorBoundary';
import { EmojiMenu } from './components/Emoji/EmojiMenu';
import { MentionMenu } from './components/Mention/MentionMenu';
import { RichEditorRefProvider } from './components/RichEditorRefProvider';
import { Toolbar } from './components/Toolbar/Toolbar';
import { RichEditorRef } from './types';
import { getNodeViewsFromExtensions } from './utils';
import classes from '@ametista-ai/editor/styles.module.css';

interface RichEditorInternalProps extends Omit<StackProps, 'onChange'> {
  initialContent?: string;
  onChange: (content: string) => void;
  templates?: RichEditorTemplate[];
  rightSection?: React.ReactNode;
  placeholder?: string;
  editorRef?: React.RefObject<RichEditorRef | null>;
}

function RichEditorInternal({
  initialContent,
  onChange,
  templates,
  rightSection,
  placeholder,
  editorRef,
  ...props
}: RichEditorInternalProps) {
  const { t } = useTranslation();
  const [mentionMenuStore] = useState(new MentionMenuStore());
  const [emojiMenuStore] = useState(new EmojiMenuStore());

  const extensions = useMemo(
    () => [
      ...ALL_NODES,
      ...ALL_MARKS,
      new MentionMenuTrigger(mentionMenuStore),
      new EmojiMenuTrigger(emojiMenuStore),
      new Placeholder(placeholder ?? t('ui.write-something')),
      new PasteHandler(),
    ],
    [emojiMenuStore, mentionMenuStore, placeholder, t]
  );

  const nodeViews = useMemo(() => getNodeViewsFromExtensions(extensions), [extensions]);

  const [editorState, setEditorState] = useState(() => {
    const schema = createSchema(extensions);
    const plugins = getPluginsFromExtensions(extensions, schema);
    const extensionsInputRules = getInputRulesFromExtensions(extensions, schema);
    const extensionsCommands = getCommandsFromExtensions(extensions, schema);

    return EditorState.create({
      schema,
      doc: initialContent ? parseContentToDoc(initialContent, schema) : undefined,
      plugins: [
        ...plugins,
        keymap({
          ...baseKeymap,
          ...extensionsCommands,
          'Mod-z': undo,
          'Mod-Shift-z': redo,
          'Mod-y': redo,
        }),
        gapCursor(),
        inputRules({
          rules: [wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.list), ...extensionsInputRules],
        }),
        history(),
        reactKeys(),
      ],
    });
  });

  const { className, ...rest } = props;

  return (
    <MentionMenuContext.Provider value={mentionMenuStore}>
      <EmojiMenuContext.Provider value={emojiMenuStore}>
        <ProseMirror
          state={editorState}
          nodeViews={nodeViews}
          dispatchTransaction={(tr) => {
            setEditorState((s) => s.apply(tr));

            if (tr.docChanged) {
              onChange(JSON.stringify(tr.doc.toJSON()));
            }
          }}
        >
          <Stack gap="xs" h="100%" className={`${classes.editor} ${className}`} {...rest}>
            {editorRef && <RichEditorRefProvider editorRef={editorRef} />}
            <Toolbar templates={templates} rightSection={rightSection} />
            <ProseMirrorDoc data-autofocus />
          </Stack>
          <MentionMenu />
          <EmojiMenu />
        </ProseMirror>
      </EmojiMenuContext.Provider>
    </MentionMenuContext.Provider>
  );
}

export function RichEditor(props: RichEditorInternalProps) {
  return (
    <ErrorBoundary
      getErrorContext={() => ({
        component: 'RichEditor',
        initialContent: props.initialContent,
      })}
    >
      <RichEditorInternal {...props} />
    </ErrorBoundary>
  );
}
